import { Controller, useForm } from 'react-hook-form';
import { Box, Checkbox, FormControlLabel, FormGroup, List, Typography } from '@mui/material';
import { Modal } from '@brainpool/components/molecules/Modal/Modal';
import { Draggable } from '@brainpool/components/molecules/Draggable/Draggable';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { ModalProps } from '../../../types/ModalTypes';
import ModalButtons from '../../ModalButtons/ModalButtons';
import { useGetUser } from '../../../hooks/useGetUser';
import { getSavedColumns } from '../../../utils/getSavedColumns';
import { useUpdateUser } from '../../../hooks/useUpdateUser';
import { ColumnProps } from '../../../types/CustomColumns';

export interface FormData {
  sector: boolean;
  fitScore: boolean;
  ups: boolean;
  tech: boolean;
  patents: boolean;
  team: boolean;
  dataCoverage: boolean;
  risks: boolean;
}

export function CustomiseColumns({ handleClose, open = false }: ModalProps) {
  const { getUser, data } = useGetUser({ useCache: true });
  const { updateUser } = useUpdateUser();

  useEffect(() => {
    getUser();
  }, []);
  // get table preferences from user data
  const { user_preferences: { table } = {} } = data?.getUser || {};
  const savedColumns = useMemo(() => getSavedColumns(table), [table]);

  // state
  const [columns, setColumns] = useState<ColumnProps[]>(savedColumns);

  // Update columns state when savedColumns changes
  useEffect(() => {
    setColumns(savedColumns);
  }, [savedColumns]);

  const onSubmit = async (formData: FormData) => {
    //merge formData with columns
    const mergedColumns = columns.map((column, index) => ({
      ...column,
      order: index,
      display: formData[column.id as keyof FormData],
    }));
    updateUser({
      user_preferences: {
        table: mergedColumns,
      },
    }).then(() => {
      handleClose();
    });
  };

  // Build savedColumns as an object mapping column ids to their display boolean
  const buildsavedColumns = useCallback(() => {
    const base: Partial<FormData> = {};
    savedColumns.forEach(col => {
      base[col.id as keyof FormData] = col.display;
    });
    return base;
  }, [savedColumns]);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    reset,
  } = useForm<FormData>({
    defaultValues: buildsavedColumns(),
    mode: 'onChange',
  });

  // Reset form when savedColumns change
  useEffect(() => {
    reset(buildsavedColumns());
  }, [buildsavedColumns, reset]);

  const renderColumn = ({ id, label }: ColumnProps) => {
    return (
      <FormGroup sx={{ mb: 0 }}>
        <Controller
          name={id as keyof FormData}
          control={control}
          render={({ field: { onChange, value } }) => (
            <FormControlLabel
              control={
                <Checkbox
                  id="terms"
                  checked={!!value}
                  onChange={e => onChange(e.target.checked)}
                  sx={{ p: 0, mr: 1 }}
                />
              }
              label={label}
            />
          )}
        />
      </FormGroup>
    );
  };

  if (!data) {
    return null;
  }

  return (
    <Modal
      titleType="h3"
      title="Customise"
      open={open}
      onClose={handleClose}
      children={
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          data-testid="customise-columns-modal"
        >
          <Typography sx={{ mb: 2 }}>
            Select which columns to display and reorder them to tailor the table to your workflow.
          </Typography>
          <List sx={{ width: '100%', p: 2 }}>
            <Draggable items={columns} renderItem={renderColumn} setItems={setColumns} />
          </List>
        </Box>
      }
      actions={
        <ModalButtons onCancel={handleClose} onConfirm={handleSubmit(onSubmit)} isValid={isValid} />
      }
    />
  );
}

export default CustomiseColumns;
